import { useCallback, type ComponentType, type ReactNode } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import AiAnalysisPdfDownloadMenu from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisPdfDownloadMenu';
import aiContentToolsApi, {
  useGetAiSearchDetailsQuery
} from '@/app/ContentTools/api/aiContentAnalysisApi';
import GeoScoreCard from '@/app/ContentTools/components/ai-content-analysis/GeoScoreCard';
import { AiAnalysisReportSkeleton } from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSkeletons';
import mapAiSearchData from '@/app/ContentTools/utils/mapAiSearchData';
import { useAiDetectionSocket } from '@/app/ContentTools/hooks/useAiDetectionSocket';

type FalconCardHeaderProps = {
  className?: string;
  light?: boolean;
  title: ReactNode;
  endEl?: ReactNode;
};

type ApiErrorResponse = {
  status?: number;
  data?: {
    code?: number;
    msg?: string;
  };
};

type GeoAnalysisDetailsPanelProps = {
  analysisId: string;
  markdownReport: ComponentType<{ message: string }>;
};

const TypedFalconCardHeader =
  FalconCardHeader as ComponentType<FalconCardHeaderProps>;

const GeoAnalysisDetailsPanel = ({
  analysisId,
  markdownReport: MarkdownReport
}: GeoAnalysisDetailsPanelProps) => {
  const dispatch = useDispatch();
  const { data: detailsResponse, isLoading, error, isError } =
    useGetAiSearchDetailsQuery({ id: analysisId }, { skip: !analysisId });

  const detailsData = detailsResponse?.data;
  const geoStatus = detailsData?.ai_search_status;
  const isGeoPending = geoStatus != null && geoStatus !== 'finished';

  const revalidateGeoApis = useCallback(() => {
    dispatch(
      aiContentToolsApi.util.invalidateTags([
        { type: 'AiContentOverview', id: analysisId },
        { type: 'AiGeoDetails', id: analysisId }
      ])
    );
  }, [analysisId, dispatch]);

  useAiDetectionSocket({
    historyId: analysisId,
    active: Boolean(analysisId && isGeoPending),
    watchStatuses: ['ai_search_status'],
    onFinished: revalidateGeoApis
  });

  const errorData = (error as ApiErrorResponse | undefined)?.data;
  const isNotFound =
    detailsResponse?.code === 400 ||
    detailsResponse?.msg === 'آیتم موردنظر پیدا نشد.' ||
    (isError &&
      ((error as ApiErrorResponse)?.status === 400 ||
        errorData?.code === 400 ||
        errorData?.msg === 'آیتم موردنظر پیدا نشد.'));

  const geo = mapAiSearchData(detailsData);
  const isGeoMissing =
    Boolean(detailsData) &&
    detailsData?.ai_search_status == null &&
    detailsData?.ai_search == null &&
    detailsData?.ai_search_report == null;

  if (isLoading) {
    return <AiAnalysisReportSkeleton />;
  }

  if (!geo && isGeoPending) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner
            animation="border"
            variant="primary"
            className="mb-3"
            style={{ width: 28, height: 28 }}
          />
          <p className="fw-semi-bold mb-1">در حال انجام تحلیل GEO...</p>
          <p className="text-600 mb-0 fs--1">
            به‌محض آماده‌شدن نتیجه، این صفحه به‌روز می‌شود.
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (!geo && (isNotFound || isGeoMissing)) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="fw-semi-bold mb-0">برای این مورد تحلیل GEO یافت نشد.</p>
        </Card.Body>
      </Card>
    );
  }

  if (!geo) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="text-600 mb-0">
            {detailsResponse?.msg || 'در دریافت جزئیات تحلیل GEO خطایی رخ داد.'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col xs={12} lg={8} className="order-1 order-lg-0">
        <Card className="w-100">
          <TypedFalconCardHeader
            className="fs--1"
            light
            title={
              <span className="text-muted fw-bold fs-0">گزارش GEO</span>
            }
            endEl={
              <AiAnalysisPdfDownloadMenu
                id={analysisId}
                currentType="ai_search"
              />
            }
          />
          <Card.Body>
            {geo.message ? (
              <MarkdownReport message={geo.message} />
            ) : (
              <p className="text-600 mb-0">گزارش متنی برای این تحلیل موجود نیست.</p>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={4} className="order-0 order-lg-1">
        <div
          className="sticky-top"
          style={{
            top: '1rem',
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto'
          }}
        >
          <GeoScoreCard data={geo} />
        </div>
      </Col>
    </Row>
  );
};

export default GeoAnalysisDetailsPanel;
